import { cloneDeep } from 'lodash'
import { _getPRDataById, getProductListData } from '../products/mockData'
import {
  getSupplierProductListData,
  getSupplierProductData,
  getSupplierProductWithSupplierProductItemsData,
  _getSPDataById,
} from '../supplierProducts/mockData'
import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import { IEntitySelectItem, assertIsString } from './../../../../types/models'
import type { ISupplierProductItem } from '@/types/supplierProductItem/models'
import type { ISupplierProduct } from '~/types/supplierProduct/models'
import type { IItemProduct, IKitProduct } from '~/types/product/models'
import { getRecursPaidIntervalByLabel } from '~/services/selectLists'
import {
  SupplierProductItemStatusIds,
  getSupplierProductItemStatusById,
} from '~/services/selectLists/supplierProductItemStatii'

const supplierProductItemListData =
  undefined as unknown as ISupplierProductItem[]

export const getSupplierProductItemListData = getListDataFunctionFactory<
  ISupplierProductItem[]
>({
  listData: supplierProductItemListData,
  storageKey: 'supplierproductitems',
  getStartingListData: getStartingSupplierProductItemListData,
})

export async function getSupplierProductItemsDataForSupplierProduct({
  id,
  doRecurse,
  doReplaceSupplierProductInSupplierProductItemsWithEntitySelectItem,
}: {
  id: string
  doRecurse: boolean
  doReplaceSupplierProductInSupplierProductItemsWithEntitySelectItem: boolean
}) {
  const fn = 'getSupplierProductItemsDataForSupplierProduct |'
  console.debug(
    `${fn} called with supplierProductId: ${id},` +
      ` doRecurse: ${doRecurse}` +
      ` doReplaceSupplierProductInSupplierProductItemsWithEntitySelectItem:` +
      ` ${doReplaceSupplierProductInSupplierProductItemsWithEntitySelectItem}`
  )
  const spiList = await getSupplierProductItemListData()
  const spiListCopy = cloneDeep(spiList) // To avoid aliasing
  const filteredSPItemListData: ISupplierProductItem[] = spiListCopy.filter(
    (spi: ISupplierProductItem) => spi.supplierProduct?.id === id
  )
  console.debug(`${fn} filteredSPItemsListData:`, filteredSPItemListData)
  const finalSPItemList = []
  for (const spi of filteredSPItemListData) {
    const spiCopy = cloneDeep(spi) // Just to log value at this point
    console.debug(`${fn} spi:`, spiCopy)
    if (doReplaceSupplierProductInSupplierProductItemsWithEntitySelectItem) {
      const label = spi.supplierProduct.descr
        ? spi.supplierProduct.descr
        : spi.supplierProduct.product.descr
      const supplierProductEntitySelectItem: IEntitySelectItem = {
        id: spi.supplierProduct.id,
        label,
      }
      spi.supplierProduct = supplierProductEntitySelectItem
      console.debug(`${fn} spi (after swapping):`, spi)
    }
    finalSPItemList.push(spi)
    console.debug(`${fn} finalSPItemList (after adding sp):`, finalSPItemList)
    if (spi.childSupplierProduct) {
      assertIsString(spi.childSupplierProduct.id)
      if (doRecurse) {
        console.debug(
          `${fn} doRecurse: ${doRecurse}. Recursing (getting ${spi.childSupplierProduct.id})...`
        )
        spi.childSupplierProduct =
          await getSupplierProductWithSupplierProductItemsData({
            id: spi.childSupplierProduct.id,
            doReplaceSupplierProductInSupplierProductItemsWithEntitySelectItem,
          })
      } else {
        spi.childSupplierProduct = await getSupplierProductData(
          spi.childSupplierProduct.id
        )
      }
    }
  }
  finalSPItemList.sort((a, b) => a.seqNum - b.seqNum)
  console.debug(`${fn} returning:`, finalSPItemList)
  return finalSPItemList
}

export async function getRootLevelSupplierProductItemsCountForSupplierProduct(
  id: string
): Promise<number> {
  const fn = 'getRootLevelSupplierProductItemsCountForSupplierProduct |'
  console.debug(`${fn} called with supplierProductId: ${id}`)
  const spiList = await getSupplierProductItemListData()
  const filteredSPItemListData: ISupplierProductItem[] = spiList.filter(
    (spi: ISupplierProductItem) => spi.supplierProduct?.id === id
  )
  const filteredSPItemListDataCount = filteredSPItemListData?.length ?? 0
  console.debug(
    `${fn} returning filteredSPItemListData:`,
    filteredSPItemListDataCount
  )
  return filteredSPItemListDataCount
}

export async function getStartingSupplierProductItemListData(): Promise<
  ISupplierProductItem[]
> {
  const prList = await getProductListData()
  const isInitializingKitItemData = false
  const pr0001 = (await _getPRDataById(
    'XXPR0001',
    prList,
    isInitializingKitItemData
  )) as IItemProduct
  const pr0003 = (await _getPRDataById(
    'XXPR0003',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // SDWAN License
  const pr0005 = (await _getPRDataById(
    'XXPR0005',
    prList,
    isInitializingKitItemData
  )) as IItemProduct
  const pr0006 = (await _getPRDataById(
    'XXPR0006',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // POTS
  const pr0007 = (await _getPRDataById(
    'XXPR0007',
    prList,
    isInitializingKitItemData
  )) as IItemProduct
  const pr0009 = (await _getPRDataById(
    'XXPR0009',
    prList,
    isInitializingKitItemData
  )) as IItemProduct
  const pr0013 = (await _getPRDataById(
    'XXPR0013',
    prList,
    isInitializingKitItemData
  )) as IItemProduct
  const pr0014 = (await _getPRDataById(
    'XXPR0014',
    prList,
    isInitializingKitItemData
  )) as IItemProduct
  const pr0016 = (await _getPRDataById(
    'XXPR0016',
    prList,
    isInitializingKitItemData
  )) as IItemProduct
  const pr0017 = (await _getPRDataById(
    'XXPR0017',
    prList,
    isInitializingKitItemData
  )) as IItemProduct
  const pr0018 = (await _getPRDataById(
    'XXPR0018',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // E-LAN Circuit
  const pr0020 = (await _getPRDataById(
    'XXPR0020',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // V10Box
  const pr0021 = (await _getPRDataById(
    'XXPR0021',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // V120Box
  const pr0022 = (await _getPRDataById(
    'XXPR0022',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // V180Box
  const pr0023 = (await _getPRDataById(
    'XXPR0023',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // V1800Box
  const pr0064 = (await _getPRDataById(
    'XXPR0064',
    prList,
    isInitializingKitItemData
  )) as IKitProduct // SDWAN Device (Set)
  const pr0065 = (await _getPRDataById(
    'XXPR0065',
    prList,
    isInitializingKitItemData
  )) as IKitProduct // Managed Router (Set)
  const pr0066 = (await _getPRDataById(
    'XXPR0066',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // Router - Cisco ISR
  const pr0067 = (await _getPRDataById(
    'XXPR0067',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // Router - Juniper SRX
  const pr0069 = (await _getPRDataById(
    'XXPR0069',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // Wi-Fi (Kit)
  const pr0070 = (await _getPRDataById(
    'XXPR0070',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // Wi-Fi Mounting (Kit)
  const pr0071 = (await _getPRDataById(
    'XXPR0071',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // Wi-Fi Access Point
  const pr0072 = (await _getPRDataById(
    'XXPR0072',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // Wi-Fi Controller License
  const pr0073 = (await _getPRDataById(
    'XXPR0073',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // Mounting Bracket
  const pr0075 = (await _getPRDataById(
    'XXPR0075',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // Data Loss Protection (DLP) (Kit)
  const pr0076 = (await _getPRDataById(
    'XXPR0076',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // DLP Licensing (Set)
  const pr0077 = (await _getPRDataById(
    'XXPR0077',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // SASE Support & Implementation (Set)
  const pr0078 = (await _getPRDataById(
    'XXPR0078',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // Zscaler
  const pr0026 = (await _getPRDataById(
    'XXPR0026',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // UCaaS - Standard License
  const pr0027 = (await _getPRDataById(
    'XXPR0027',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // UCaaS - Comp & Admin Cost Recovery Fee
  const pr0028 = (await _getPRDataById(
    'XXPR0028',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // UCaaS - e911 Service
  const pr0029 = (await _getPRDataById(
    'XXPR0029',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // UCaaS - TAM - gold
  const pr0030 = (await _getPRDataById(
    'XXPR0030',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // UCaaS - TAM - silver
  const pr0031 = (await _getPRDataById(
    'XXPR0031',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // UCaaS - TAM - bronze
  const pr0032 = (await _getPRDataById(
    'XXPR0032',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // UCaaS - TAM - kit

  const pr0036 = (await _getPRDataById(
    'XXPR0036',
    prList,
    isInitializingKitItemData
  )) as IKitProduct // DIA Bandwidth
  const pr0050 = (await _getPRDataById(
    'XXPR0050',
    prList,
    isInitializingKitItemData
  )) as IKitProduct // DIA Access
  const pr0054 = (await _getPRDataById(
    'XXPR0054',
    prList,
    isInitializingKitItemData
  )) as IKitProduct // IP Addresses
  const pr0060 = (await _getPRDataById(
    'XXPR0060',
    prList,
    isInitializingKitItemData
  )) as IKitProduct // DIA - CPE

  const pr0040 = (await _getPRDataById(
    'XXPR0040',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // PORT - 100Mb
  const pr0042 = (await _getPRDataById(
    'XXPR0042',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // PORT - 200Mb
  const pr0043 = (await _getPRDataById(
    'XXPR0043',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // PORT - 5000Mb
  const pr0044 = (await _getPRDataById(
    'XXPR0044',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // PORT - 1Gb

  const pr0051 = (await _getPRDataById(
    'XXPR0051',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // Access - 1Gb

  const pr0052 = (await _getPRDataById(
    'XXPR0052',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // Access - 10Gb

  const pr0056 = (await _getPRDataById(
    'XXPR0056',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // IP Address Block - /29

  const pr0058 = (await _getPRDataById(
    'XXPR0058',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // IP Address Block - /31

  const pr0059 = (await _getPRDataById(
    'XXPR0059',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // IP Address Block - Customer Owned BGP

  const pr0061 = (await _getPRDataById(
    'XXPR0061',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // DIA CPE - Customer Provided

  const pr0062 = (await _getPRDataById(
    'XXPR0062',
    prList,
    isInitializingKitItemData
  )) as IItemProduct // DIA CPE - Fiber Converter

  const spList = await getSupplierProductListData()
  const sp0003 = (await _getSPDataById('XXSP0003', spList)) as ISupplierProduct // Cogent DIA (Kit)
  const sp0004 = (await _getSPDataById('XXSP0004', spList)) as ISupplierProduct // Cogent E-LAN
  const sp0005 = (await _getSPDataById('XXSP0005', spList)) as ISupplierProduct // Comcast DIA (Kit)
  const sp0007 = (await _getSPDataById('XXSP0007', spList)) as ISupplierProduct // Cogent E-LAN Hub (Kit)
  const sp0008 = (await _getSPDataById('XXSP0008', spList)) as ISupplierProduct // Cogent E-LAN Endpoint (Kit)
  const sp0009 = (await _getSPDataById('XXSP0009', spList)) as ISupplierProduct // Cogent E-LAN Circuit
  const sp0010 = (await _getSPDataById('XXSP0010', spList)) as ISupplierProduct // Coeo SDWAN (Kit)
  const sp0023 = (await _getSPDataById('XXSP0023', spList)) as ISupplierProduct // Coeo SDWAN Device (Set)
  const sp0012 = (await _getSPDataById('XXSP0012', spList)) as ISupplierProduct // Ring Central UCaaS (Kit)
  const sp0013 = (await _getSPDataById('XXSP0013', spList)) as ISupplierProduct // Ring Central TAM (Set)
  const sp0014 = (await _getSPDataById('XXSP0014', spList)) as ISupplierProduct // WXTG Consulting Services
  const sp0015 = (await _getSPDataById('XXSP0015', spList)) as ISupplierProduct // Pac-Tel POTS Line
  const sp0016 = (await _getSPDataById('XXSP0016', spList)) as ISupplierProduct // Cogent DIA Bandwidth (Set)
  const sp0017 = (await _getSPDataById('XXSP0017', spList)) as ISupplierProduct // Cogent DIA Access (set)
  const sp0018 = (await _getSPDataById('XXSP0018', spList)) as ISupplierProduct // Cogent IP Address Block
  const sp0019 = (await _getSPDataById('XXSP0019', spList)) as ISupplierProduct // Cogent DIA CPE (Set)
  const sp0020 = (await _getSPDataById('XXSP0020', spList)) as ISupplierProduct // Comcast DIA Bandwidth (Set)
  const sp0021 = (await _getSPDataById('XXSP0021', spList)) as ISupplierProduct // Comcast DIA Access (Set)
  const sp0022 = (await _getSPDataById('XXSP0022', spList)) as ISupplierProduct // Comcast DIA CPE (Set)
  const sp0024 = (await _getSPDataById('XXSP0024', spList)) as ISupplierProduct // Cogent Managed Router (Set)
  const sp0025 = (await _getSPDataById('XXSP0025', spList)) as ISupplierProduct // Cogent Branch Office (Kit)
  const sp0026 = (await _getSPDataById('XXSP0026', spList)) as ISupplierProduct // Cogent Wi-Fi (Kit)
  const sp0027 = (await _getSPDataById('XXSP0027', spList)) as ISupplierProduct // Cogent Wi-Fi Mounting (Kit)
  const sp0028 = (await _getSPDataById('XXSP0028', spList)) as ISupplierProduct // AppDirect Zscaler DLP - 3 Year (Kit)
  const sp0029 = (await _getSPDataById('XXSP0029', spList)) as ISupplierProduct // AppDirect Data Loss Protection (DLP) (Kit)
  const sp0030 = (await _getSPDataById('XXSP0030', spList)) as ISupplierProduct // AppDirect DLP Licensing (Set)
  const sp0031 = (await _getSPDataById('XXSP0031', spList)) as ISupplierProduct // AppDirect SASE Support & Implementation (Set)

  // TERM: `null` ("None") on every item except two fixtures, because an item's
  // stored term is now the EXCEPTION -- an msService normally takes its service
  // group's defaultTerm (resolveMSServiceTerm). The two that keep one:
  //   - Ring Central UCaaS / TAM (XXSPI0029-0031, 0033-0035): 72 -- a whole kit
  //     whose stored term overrides the default;
  //   - Cogent IP Address Block (XXSPI0044-0046): 36 -- ONE item inside the DIA
  //     kit overriding it. Against XXO0003's default of 24 that kit reads
  //     "Varies".
  return [
    // {
    //   id: 'XXSPI0004',
    //   hrid: 'SPI0004',
    //   supplierProduct: {
    //     id: 'XXSP0003',
    //     label: 'DIA 1Gb',
    //   },
    //   product: pr0001,
    //   isOptional: false,
    //   splitFraction: 0.18,
    //   term: 36,
    //   recurs: getRecursPaidIntervalByLabel('Monthly'),
    //   paid: getRecursPaidIntervalByLabel('Monthly'),
    //   recursPaidDescr: 'M / M',
    //   quantity: 'kit',
    //   unitRcAmount: 1300,
    //   unitNrcAmount: 200,
    //   oneTimeAmount: 0,
    //   doMultiplyNrcByQuantity: false,
    //   doCopyDollarAmountsToServices: true,
    //   quoteDate: '2022-10-01',
    //   status: getSupplierProductItemStatusById(
    //     SupplierProductItemStatusIds.ACTIVE
    //   ),
    //   canBeDeleted: false,
    // },
    {
      id: 'XXSPI0004',
      hrid: 'SPI0004',
      supplierProduct: sp0003, // Cogent DIA (Kit)
      product: pr0036, // DIA Bandwidth (Set)
      childSupplierProduct: sp0016, // Cogent DIA Bandwidth (Set)
      quantity: 'kit',
      seqNum: 1,
      version: '1',
      canBeDeleted: false,
    },
    // {
    //   id: 'XXSPI0005',
    //   hrid: 'SPI0005',
    //   supplierProduct: {
    //     id: 'XXSP0003',
    //     label: 'DIA 1Gb',
    //   },
    //   product: pr0007,
    //   isOptional: true,
    //   splitFraction: 0.12,
    //   term: 36,
    //   recurs: getRecursPaidIntervalByLabel('Monthly'),
    //   paid: getRecursPaidIntervalByLabel('Monthly'),
    //   recursPaidDescr: 'M / M',
    //   quantity: 'x2',
    //   unitRcAmount: 350,
    //   unitNrcAmount: 210,
    //   oneTimeAmount: 0,
    //   doMultiplyNrcByQuantity: false,
    //   doCopyDollarAmountsToServices: true,
    //   quoteDate: '2022-10-01',
    //   status: getSupplierProductItemStatusById(
    //     SupplierProductItemStatusIds.ACTIVE
    //   ),
    //   canBeDeleted: false,
    // },
    {
      id: 'XXSPI0005',
      hrid: 'SPI0005',
      supplierProduct: sp0003, // Cogent DIA (Kit)
      product: pr0050, // DIA Access (Set)
      childSupplierProduct: sp0017, // Cogent DIA Access (Set)
      quantity: 'kit',
      seqNum: 2,
      version: '1',
      canBeDeleted: false,
    },
    // {
    //   id: 'XXSPI0006',
    //   hrid: 'SPI0006',
    //   supplierProduct: {
    //     id: 'XXSP0003',
    //     label: 'DIA 1Gb',
    //   },
    //   product: pr0012,
    //   isOptional: false,
    //   splitFraction: 0.1,
    //   term: 36,
    //   recurs: getRecursPaidIntervalByLabel('Monthly'),
    //   paid: getRecursPaidIntervalByLabel('Monthly'),
    //   recursPaidDescr: 'M / M',
    //   quantity: 'kit',
    //   unitRcAmount: 45,
    //   unitNrcAmount: 0,
    //   oneTimeAmount: 0,
    //   doMultiplyNrcByQuantity: false,
    //   doCopyDollarAmountsToServices: true,
    //   quoteDate: '2022-10-01',
    //   status: getSupplierProductItemStatusById(
    //     SupplierProductItemStatusIds.ACTIVE
    //   ),
    //   canBeDeleted: false,
    // },
    {
      id: 'XXSPI0006',
      hrid: 'SPI0006',
      supplierProduct: sp0003, // Cogent DIA (Kit)
      product: pr0054, // IP Address (Set)
      childSupplierProduct: sp0018, // Cogent IP Address (Set)
      quantity: 'kit',
      seqNum: 3,
      version: '1',
      canBeDeleted: false,
    },

    // {
    //   id: 'XXSPI0007',
    //   hrid: 'SPI0007',
    //   supplierProduct: {
    //     id: 'XXSP0005',
    //     label: 'DIA 1Gb',
    //   },
    //   product: pr0001,
    //   isOptional: false,
    //   splitFraction: 0.18,
    //   term: 36,
    //   recurs: getRecursPaidIntervalByLabel('Monthly'),
    //   paid: getRecursPaidIntervalByLabel('Monthly'),
    //   recursPaidDescr: 'M / M',
    //   quantity: 'kit',
    //   unitRcAmount: 1100,
    //   unitNrcAmount: 370,
    //   oneTimeAmount: 0,
    //   doMultiplyNrcByQuantity: false,
    //   doCopyDollarAmountsToServices: true,
    //   quoteDate: '2022-10-01',
    //   status: getSupplierProductItemStatusById(
    //     SupplierProductItemStatusIds.ACTIVE
    //   ),
    //   canBeDeleted: false,
    // },
    {
      id: 'XXSPI0007',
      hrid: 'SPI0007',
      supplierProduct: sp0005, // Comcast DIA (Kit)
      product: pr0036, // DIA Bandwidth (Set)
      childSupplierProduct: sp0020, // Comcast DIA Bandwidth (Set)
      quantity: 'kit',
      seqNum: 1,
      version: '1',
      canBeDeleted: false,
    },
    // {
    //   id: 'XXSPI0008',
    //   hrid: 'SPI0008',
    //   supplierProduct: {
    //     id: 'XXSP0005',
    //     label: 'DIA 1Gb',
    //   },
    //   product: pr0007,
    //   isOptional: true,
    //   splitFraction: 0.12,
    //   term: 36,
    //   recurs: getRecursPaidIntervalByLabel('Monthly'),
    //   paid: getRecursPaidIntervalByLabel('Monthly'),
    //   recursPaidDescr: 'M / M',
    //   quantity: 'kit',
    //   unitRcAmount: 900,
    //   unitNrcAmount: 100,
    //   oneTimeAmount: 0,
    //   doMultiplyNrcByQuantity: false,
    //   doCopyDollarAmountsToServices: true,
    //   quoteDate: '2022-10-01',
    //   status: getSupplierProductItemStatusById(
    //     SupplierProductItemStatusIds.ACTIVE
    //   ),
    //   canBeDeleted: false,
    // },
    {
      id: 'XXSPI0008',
      hrid: 'SPI0008',
      supplierProduct: sp0005, // Comcast DIA (Kit)
      product: pr0050, // DIA Access (Set)
      childSupplierProduct: sp0021, // Comcasst DIA Access (Set)
      quantity: 'kit',
      seqNum: 2,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0012',
      hrid: 'SPI0012',
      supplierProduct: sp0004, // Cogent E-LAN (kit)
      product: pr0007, // E-LAN (Kit)
      descr: `${sp0004.supplier.descr} ${pr0007.descr}`,
      isOptional: true,
      splitFraction: 0.1,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'x2',
      unitRcAmount: 50,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2024-01-04',
      seqNum: 1,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0013',
      hrid: 'SPI0013',
      supplierProduct: sp0004, // Cogent E-LAN (Kit)
      product: pr0009, // Consulting Services
      descr: `${sp0004.supplier.descr} ${pr0009.descr}`,
      isOptional: false,
      splitFraction: 0.1,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: '1',
      unitRcAmount: 50,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2024-01-04',
      seqNum: 2,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0014',
      hrid: 'SPI0014',
      supplierProduct: sp0004, // Cogent E-LAN (Kit)
      product: pr0016, // E-LAN Hub (Kit)
      descr: 'Hub1',
      childSupplierProduct: sp0007, // Cogent E-LAN Hub (Kit)
      quantity: 'kit',
      seqNum: 3,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0015',
      hrid: 'SPI0015',
      supplierProduct: sp0004, // Cogent E-LAN (Kit)
      product: pr0016, // E-LAN Hub (Kit)
      descr: 'Hub2',
      childSupplierProduct: sp0007, // Cogent E-LAN Hub (Kit)
      quantity: 'kit',
      seqNum: 4,
      version: '1',
      canBeDeleted: false,
    },
    // {
    //   id: 'XXSPI0016',
    //   hrid: 'SPI0016',
    //   supplierProduct: {
    //     id: 'XXSP0007',
    //     label: 'E-LAN Hub',
    //   },
    //   product: pr0018,
    //   isOptional: false,
    //   splitFraction: 1.0,
    //   term: 36,
    //   recurs: getRecursPaidIntervalByLabel('Monthly'),
    //   paid: getRecursPaidIntervalByLabel('Monthly'),
    //   quantity: 'kit',
    //   unitRcAmount: 1000,
    //   unitNrcAmount: 150,
    //   oneTimeAmount: 0,
    //   doMultiplyNrcByQuantity: false,
    //   doCopyDollarAmountsToServices: true,
    //   quoteDate: '2022-10-01',
    //   status: getSupplierProductItemStatusById(
    //     SupplierProductItemStatusIds.ACTIVE
    //   ),
    //   canBeDeleted: false,
    // },
    {
      id: 'XXSPI0016',
      hrid: 'SPI0016',
      supplierProduct: sp0007, // Cogent E-LAN Hub (Kit)
      product: pr0018, // E-LAN Circuit
      childSupplierProduct: sp0009, // Cogent E-LAN Circuit
      quantity: 'kit',
      seqNum: 1,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0017',
      hrid: 'SPI0017',
      supplierProduct: sp0007, // Cogent E-LAN Hub (Kit)
      product: pr0005, // Edge Router
      descr: `${sp0007.supplier.descr} ${pr0005.descr}`,
      isOptional: true,
      splitFraction: 0.1,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 75,
      unitNrcAmount: 100,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2024-01-04',
      seqNum: 2,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0018',
      hrid: 'SPI0018',
      supplierProduct: sp0007, // Cogent E-LAN Hub (Kit)
      product: pr0017, // E-LAN Endpoint (Kit)
      childSupplierProduct: sp0008, // Cogent E-LAN Endpoint (Kit)
      quantity: 'kit',
      seqNum: 3,
      version: '1',
      canBeDeleted: false,
    },

    // {
    //   id: 'XXSPI0019',
    //   hrid: 'SPI0019',
    //   supplierProduct: {
    //     id: 'XXSP0008',
    //     label: 'E-LAN Enpoint',
    //   },
    //   product: pr0018,
    //   isOptional: false,
    //   splitFraction: 1.0,
    //   term: 36,
    //   recurs: getRecursPaidIntervalByLabel('Monthly'),
    //   paid: getRecursPaidIntervalByLabel('Monthly'),
    //   quantity: 'kit',
    //   unitRcAmount: 1000,
    //   unitNrcAmount: 150,
    //   oneTimeAmount: 0,
    //   doMultiplyNrcByQuantity: false,
    //   doCopyDollarAmountsToServices: true,
    //   quoteDate: '2022-10-01',
    //   status: getSupplierProductItemStatusById(
    //     SupplierProductItemStatusIds.ACTIVE
    //   ),
    //   canBeDeleted: false,
    // },
    {
      id: 'XXSPI0019',
      hrid: 'SPI0019',
      supplierProduct: sp0008, // Cogent E-LAN Endpoint (Kit)
      product: pr0018, // E-LAN Circuit
      childSupplierProduct: sp0009, // Cogent E-LAN Circuit
      quantity: 'kit',
      seqNum: 1,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0020',
      hrid: 'SPI0020',
      supplierProduct: sp0008, // Cogent E-LAN Endpoint (Kit)
      product: pr0018, // E-LAN Circuit
      childSupplierProduct: sp0009, // Cogent E-LAN Circuit
      quantity: 'kit',
      seqNum: 2,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0021',
      hrid: 'SPI0021',
      supplierProduct: sp0008, // Cogent E-LAN Endpoint (Kit)
      product: pr0005, // Edge Router
      descr: `${sp0008.supplier.descr} ${pr0005.descr}`,
      isOptional: true,
      splitFraction: 0.1,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 75,
      unitNrcAmount: 100,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2024-01-04',
      seqNum: 3,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0022',
      hrid: 'SPI0022',
      supplierProduct: sp0009, // Cogent E-LAN Circuit
      product: pr0018, // E-LAN Circuit
      descr: `${sp0009.supplier.descr} ${pr0018.descr}`,
      isOptional: false,
      splitFraction: 0.12,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 45,
      unitNrcAmount: 90,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2024-02-04',
      seqNum: 1,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0023',
      hrid: 'SPI0023',
      supplierProduct: sp0010, // Coeo SDWAN (Kit)
      product: pr0009, // Consulting Services
      descr: `${sp0010.supplier.descr} ${pr0009.descr}`,
      // The kit's one free-form add-on -- plain-kit-owned, so it can only be
      // added or removed, never swapped. Matches XXPRKI0018 at product level.
      isOptional: true,
      splitFraction: 1.0,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 1000,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2024-02-04',
      seqNum: 1,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0024',
      hrid: 'SPI0024',
      supplierProduct: sp0010, // Coeo SDWAN (Kit)
      product: pr0003, // License (SDWAN)
      descr: `${sp0010.supplier.descr} ${pr0003.descr}`,
      isOptional: false,
      splitFraction: 0.2,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 450,
      unitNrcAmount: 30,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2024-02-04',
      seqNum: 2,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0025',
      hrid: 'SPI0025',
      supplierProduct: sp0023, // Coeo SDWAN Device (Set)
      product: pr0020, // V10Box
      descr: `${sp0023.supplier.descr} ${pr0020.descr}`,
      isOptional: true,
      splitFraction: 0.12,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 50,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2024-02-04',
      seqNum: 1,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0026',
      hrid: 'SPI0026',
      supplierProduct: sp0023, // Coeo SDWAN Device (Set)
      product: pr0021, // V120Box
      descr: `${sp0023.supplier.descr} ${pr0021.descr}`,
      isOptional: true,
      splitFraction: 0.12,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 50,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2024-02-04',
      seqNum: 2,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0027',
      hrid: 'SPI0027',
      supplierProduct: sp0023, // Coeo SDWAN Device (Set)
      product: pr0022, // V180Box
      descr: `${sp0023.supplier.descr} ${pr0022.descr}`,
      isOptional: true,
      splitFraction: 0.12,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 60,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2024-02-04',
      seqNum: 3,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0028',
      hrid: 'SPI0028',
      supplierProduct: sp0023, // Coeo SDWAN Device (Set)
      product: pr0023, // V1800Box
      descr: `${sp0023.supplier.descr} ${pr0023.descr}`,
      isOptional: true,
      splitFraction: 0.12,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 65,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2024-02-04',
      seqNum: 4,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0029',
      hrid: 'SPI0029',
      supplierProduct: sp0012, // Ring Central UCaaS
      product: pr0026, // Standard License
      descr: `${sp0012.supplier.descr} ${pr0026.descr}`,
      isOptional: false,
      splitFraction: 0.1955,
      term: 72,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 12,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2024-02-04',
      seqNum: 1,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0030',
      hrid: 'SPI0030',
      supplierProduct: sp0012, // Ring Central UCaaS
      product: pr0028, // Compliance and Admin Cost Recovery Fee
      descr: `${sp0012.supplier.descr} ${pr0028.descr}`,
      isOptional: false,
      splitFraction: 0,
      term: 72,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 3.5,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2024-02-04',
      seqNum: 2,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0031',
      hrid: 'SPI0031',
      supplierProduct: sp0012, // Ring Central UCaaS
      product: pr0027, // e911 Fee
      descr: `${sp0012.supplier.descr} ${pr0027.descr}`,
      isOptional: false,
      splitFraction: 0,
      term: 72,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 1,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2024-02-04',
      seqNum: 3,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0032',
      hrid: 'SPI0032',
      supplierProduct: sp0012, // Ring Central UCaaS
      product: pr0032, // TAM (Set)
      childSupplierProduct: sp0013, // Ring Central TAM (Set)
      quantity: 'kit',
      seqNum: 4,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0033',
      hrid: 'SPI0033',
      supplierProduct: sp0013, // Ring Central TAM (Set)
      product: pr0031, // TAM - Bronze
      descr: `${sp0013.supplier.descr} ${pr0031.descr}`,
      isOptional: true,
      splitFraction: 0,
      term: 72,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: '1',
      unitRcAmount: 2070,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2024-02-04',
      seqNum: 1,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0034',
      hrid: 'SPI0034',
      supplierProduct: sp0013, // Ring Central TAM (Set)
      product: pr0029, // TAM - Silver
      descr: `${sp0013.supplier.descr} ${pr0029.descr}`,
      isOptional: true,
      splitFraction: 0,
      term: 72,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: '1',
      unitRcAmount: 2070,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2024-02-04',
      seqNum: 2,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0035',
      hrid: 'SPI0035',
      supplierProduct: sp0013, // Ring Central TAM (Set)
      product: pr0030, // TAM - Gold
      descr: `${sp0013.supplier.descr} ${pr0030.descr}`,
      isOptional: true,
      splitFraction: 0,
      term: 72,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: '1',
      unitRcAmount: 2070,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2024-02-04',
      seqNum: 3,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0036',
      hrid: 'SPI0036',
      supplierProduct: sp0014, // WXTG Consulting Services
      product: pr0009, // Consulting Services
      descr: `${sp0014.supplier.descr} ${pr0009.descr}`,
      isOptional: false,
      splitFraction: 1.0,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: '100',
      unitRcAmount: 250,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2024-03-04',
      seqNum: 1,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0037',
      hrid: 'SPI0037',
      supplierProduct: sp0015, // Pac-Tel POTS Line
      product: pr0006, // POTS Line
      descr: `${sp0015.supplier.descr} ${pr0006.descr}`,
      isOptional: false,
      splitFraction: 0.1,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: '100',
      unitRcAmount: 50,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2024-03-04',
      seqNum: 1,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    //
    {
      id: 'XXSPI0038',
      hrid: 'SPI0038',
      supplierProduct: sp0016, // Cogent DIA Bandwidth (Set)
      product: pr0040, // Port - 100Mb
      descr: `${sp0016.supplier.descr} ${pr0040.descr}`,
      isOptional: true,
      splitFraction: 0.18,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 30,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2022-10-01',
      seqNum: 1,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0039',
      hrid: 'SPI0039',
      supplierProduct: sp0016, // Cogent DIA Bandwidth (Set)
      product: pr0042, // Port - 200Mb
      descr: `${sp0016.supplier.descr} ${pr0042.descr}`,
      isOptional: true,
      splitFraction: 0.18,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 40,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2022-10-01',
      seqNum: 2,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0040',
      hrid: 'SPI0040',
      supplierProduct: sp0016, // Cogent DIA Bandwidth (Set)
      product: pr0043, // Port - 500Mb
      descr: `${sp0016.supplier.descr} ${pr0043.descr}`,
      isOptional: true,
      splitFraction: 0.18,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 50,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2022-10-01',
      seqNum: 3,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0041',
      hrid: 'SPI0041',
      supplierProduct: sp0016, // Cogent DIA Bandwidth (Set)
      product: pr0044, // Port - 1Gb
      descr: `${sp0016.supplier.descr} ${pr0044.descr}`,
      isOptional: true,
      splitFraction: 0.18,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 60,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2022-10-01',
      seqNum: 4,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0042',
      hrid: 'SPI0042',
      supplierProduct: sp0017, // Cogent DIA Access (Set)
      product: pr0051, // Access - 1Gb
      descr: `${sp0017.supplier.descr} ${pr0051.descr}`,
      isOptional: true,
      splitFraction: 0.18,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 100,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2022-10-01',
      seqNum: 1,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0043',
      hrid: 'SPI0043',
      supplierProduct: sp0017, // Cogent DIA Access (Set)
      product: pr0052, // Access - 10Gb
      descr: `${sp0017.supplier.descr} ${pr0052.descr}`,
      isOptional: true,
      splitFraction: 0.18,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 150,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2022-10-01',
      seqNum: 2,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0044',
      hrid: 'SPI0044',
      supplierProduct: sp0018, // Cogent IP Address Block (Set)
      product: pr0056, // IP Block - /19
      descr: `${sp0018.supplier.descr} ${pr0056.descr}`,
      isOptional: true,
      splitFraction: 0.18,
      term: 36,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 79,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2022-10-01',
      seqNum: 1,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0045',
      hrid: 'SPI0045',
      supplierProduct: sp0018, // Cogent IP Address Block (Set)
      product: pr0058, // IP Block - /31
      descr: `${sp0018.supplier.descr} ${pr0058.descr}`,
      isOptional: true,
      splitFraction: 0.18,
      term: 36,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 0,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2022-10-01',
      seqNum: 2,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0046',
      hrid: 'SPI0046',
      supplierProduct: sp0018, // Cogent IP Address Block (Set)
      product: pr0059, // Customer Owned - BGP
      descr: `${pr0059.descr}`,
      isOptional: true,
      splitFraction: 0.18,
      term: 36,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 0,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2022-10-01',
      seqNum: 3,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0047',
      hrid: 'SPI0047',
      supplierProduct: sp0019, // Cogent DIA CPE (Set)
      product: pr0061, // Customer Provided
      descr: `${pr0061.descr}`,
      isOptional: true,
      splitFraction: 0.18,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 0,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2022-10-01',
      seqNum: 1,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0048',
      hrid: 'SPI0048',
      supplierProduct: sp0019, // Cogent DIA CPE (Set)
      product: pr0062, // Fiber Converter
      descr: `${sp0019.supplier.descr} ${pr0062.descr}`,
      isOptional: true,
      splitFraction: 0.18,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 0,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2022-10-01',
      seqNum: 2,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0049',
      hrid: 'SPI0049',
      supplierProduct: sp0003, // Cogent DIA (Kit)
      product: pr0060, // CPE (Set)
      childSupplierProduct: sp0019, // Cogent DIA CPE (Set),
      quantity: 'kit',
      seqNum: 4,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0056',
      hrid: 'SPI0056',
      supplierProduct: sp0005, // Comcast DIA (Kit)
      product: pr0060, // CPE (Set)
      childSupplierProduct: sp0022, // Comcast CPE (Set)
      quantity: 'kit',
      seqNum: 3,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0050',
      hrid: 'SPI0050',
      supplierProduct: sp0020, // Comcast DIA Bandwidth (Set)
      product: pr0043, // Port - 500Mb
      descr: `${sp0020.supplier.descr} ${pr0043.descr}`,
      isOptional: true,
      splitFraction: 0.18,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 55,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2022-10-01',
      seqNum: 1,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0051',
      hrid: 'SPI0051',
      supplierProduct: sp0020, // Comcast DIA Bandwidth (Set)
      product: pr0044, // Port - 1Gb
      descr: `${sp0020.supplier.descr} ${pr0044.descr}`,
      isOptional: true,
      splitFraction: 0.18,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 65,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2022-10-01',
      seqNum: 2,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0052',
      hrid: 'SPI0052',
      supplierProduct: sp0021, // Comcast DIA Access (Set)
      product: pr0051, // Access - 1Gb
      descr: `${sp0021.supplier.descr} ${pr0051.descr}`,
      isOptional: true,
      splitFraction: 0.18,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 130,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2022-10-01',
      seqNum: 1,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0053',
      hrid: 'SPI0053',
      supplierProduct: sp0021, // Comcast DIA Access (Set)
      product: pr0052, // Access - 10Gb
      descr: `${sp0021.supplier.descr} ${pr0052.descr}`,
      isOptional: true,
      splitFraction: 0.18,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 170,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2022-10-01',
      seqNum: 2,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },

    {
      id: 'XXSPI0054',
      hrid: 'SPI0054',
      supplierProduct: sp0022, // Comcast DIA CPE (Set)
      product: pr0007, // Manager Router
      descr: `${sp0022.supplier.descr} ${pr0007.descr}`,
      isOptional: true,
      splitFraction: 0.18,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 0,
      unitNrcAmount: 0,
      oneTimeAmount: 1000,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2022-10-01',
      seqNum: 1,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0055',
      hrid: 'SPI0055',
      supplierProduct: sp0022, // Comcast DIA CPE (Set)
      product: pr0062, // Fiber Converter
      descr: `${sp0022.supplier.descr} ${pr0062.descr}`,
      isOptional: true,
      splitFraction: 0.18,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 0,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2022-10-01',
      seqNum: 2,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      // How a supplier-product reaches a Set: no isOptional and no pricing of
      // its own, just the link. Same shape as XXSPI0004, Cogent DIA (Kit) ->
      // Cogent DIA Bandwidth (Set).
      id: 'XXSPI0057',
      hrid: 'SPI0057',
      supplierProduct: sp0010, // Coeo SDWAN (Kit)
      product: pr0064, // SDWAN Device (Set)
      childSupplierProduct: sp0023, // Coeo SDWAN Device (Set)
      quantity: 'kit',
      seqNum: 3,
      version: '1',
      canBeDeleted: false,
    },
    {
      // A SET INSIDE A SET (see XXPR0065): Cogent's managed router as a third
      // CPE alternative beside XXSPI0047 / XXSPI0048. Shaped like XXSPI0057's
      // link to a Set, but OPTIONAL -- it is one of CPE (Set)'s alternatives,
      // so picking it (rather than Fiber Converter) is what brings the router
      // choice into play. No pricing: a Set has no service of its own.
      id: 'XXSPI0058',
      hrid: 'SPI0058',
      supplierProduct: sp0019, // Cogent DIA CPE (Set)
      product: pr0065, // Managed Router (Set)
      childSupplierProduct: sp0024, // Cogent Managed Router (Set)
      descr: `${sp0019.supplier.descr} ${pr0065.descr}`,
      isOptional: true,
      quantity: 'kit',
      seqNum: 3,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0059',
      hrid: 'SPI0059',
      supplierProduct: sp0024, // Cogent Managed Router (Set)
      product: pr0066, // Router - Cisco ISR
      descr: `${sp0024.supplier.descr} ${pr0066.descr}`,
      isOptional: true,
      splitFraction: 0.18,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 45,
      unitNrcAmount: 150,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2022-10-01',
      seqNum: 1,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0060',
      hrid: 'SPI0060',
      supplierProduct: sp0024, // Cogent Managed Router (Set)
      product: pr0067, // Router - Juniper SRX
      descr: `${sp0024.supplier.descr} ${pr0067.descr}`,
      isOptional: true,
      splitFraction: 0.18,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 60,
      unitNrcAmount: 150,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2022-10-01',
      seqNum: 2,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    // Cogent Branch Office -- the optional nested plain Kit. See XXPR0068.
    {
      id: 'XXSPI0061',
      hrid: 'SPI0061',
      supplierProduct: sp0025, // Cogent Branch Office (Kit)
      product: pr0066, // Router - Cisco ISR
      descr: `${sp0025.supplier.descr} ${pr0066.descr}`,
      isOptional: false,
      splitFraction: 0.18,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 45,
      unitNrcAmount: 150,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2022-10-01',
      seqNum: 1,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      // OPTIONAL, and a plain Kit: taking it builds its header and its contents.
      id: 'XXSPI0062',
      hrid: 'SPI0062',
      supplierProduct: sp0025, // Cogent Branch Office (Kit)
      product: pr0069, // Wi-Fi (Kit)
      descr: 'Wi-Fi',
      childSupplierProduct: sp0026, // Cogent Wi-Fi (Kit)
      isOptional: true,
      quantity: 'kit',
      seqNum: 2,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0063',
      hrid: 'SPI0063',
      supplierProduct: sp0026, // Cogent Wi-Fi (Kit)
      product: pr0071, // Wi-Fi Access Point
      descr: `${sp0026.supplier.descr} ${pr0071.descr}`,
      isOptional: false,
      splitFraction: 0.18,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'x2',
      unitRcAmount: 20,
      unitNrcAmount: 75,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2022-10-01',
      seqNum: 1,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0064',
      hrid: 'SPI0064',
      supplierProduct: sp0026, // Cogent Wi-Fi (Kit)
      product: pr0072, // Wi-Fi Controller License
      descr: `${sp0026.supplier.descr} ${pr0072.descr}`,
      isOptional: true,
      splitFraction: 0.18,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 10,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2022-10-01',
      seqNum: 2,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      // Mandatory -- but inside the optional Wi-Fi Kit, so it follows it.
      id: 'XXSPI0065',
      hrid: 'SPI0065',
      supplierProduct: sp0026, // Cogent Wi-Fi (Kit)
      product: pr0070, // Wi-Fi Mounting (Kit)
      descr: 'Mounting',
      childSupplierProduct: sp0027, // Cogent Wi-Fi Mounting (Kit)
      isOptional: false,
      quantity: 'kit',
      seqNum: 3,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0066',
      hrid: 'SPI0066',
      supplierProduct: sp0027, // Cogent Wi-Fi Mounting (Kit)
      product: pr0073, // Mounting Bracket
      descr: `${sp0027.supplier.descr} ${pr0073.descr}`,
      isOptional: false,
      splitFraction: 0.18,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      quantity: 'kit',
      unitRcAmount: 0,
      unitNrcAmount: 25,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2022-10-01',
      seqNum: 1,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    // AppDirect Zscaler DLP - 3 Year. See XXPR0074. Three slots for the SAME
    // DLP Kit: a nested header is matched to its slot by descr.
    {
      id: 'XXSPI0067',
      hrid: 'SPI0067',
      supplierProduct: sp0028, // AppDirect Zscaler DLP - 3 Year (Kit)
      product: pr0075, // Data Loss Protection (DLP) (Kit)
      descr: 'Zscaler DLP (1 of 3)',
      childSupplierProduct: sp0029, // AppDirect Data Loss Protection (DLP) (Kit)
      isOptional: false,
      quantity: 'kit',
      seqNum: 1,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0068',
      hrid: 'SPI0068',
      supplierProduct: sp0028, // AppDirect Zscaler DLP - 3 Year (Kit)
      product: pr0075, // Data Loss Protection (DLP) (Kit)
      descr: 'Zscaler DLP (2 of 3)',
      childSupplierProduct: sp0029, // AppDirect Data Loss Protection (DLP) (Kit)
      isOptional: false,
      quantity: 'kit',
      seqNum: 2,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0069',
      hrid: 'SPI0069',
      supplierProduct: sp0028, // AppDirect Zscaler DLP - 3 Year (Kit)
      product: pr0075, // Data Loss Protection (DLP) (Kit)
      descr: 'Zscaler DLP (3 of 3)',
      childSupplierProduct: sp0029, // AppDirect Data Loss Protection (DLP) (Kit)
      isOptional: false,
      quantity: 'kit',
      seqNum: 3,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0070',
      hrid: 'SPI0070',
      supplierProduct: sp0029, // AppDirect Data Loss Protection (DLP) (Kit)
      product: pr0076, // DLP Licensing (Set)
      descr: 'DLP Licensing',
      childSupplierProduct: sp0030, // AppDirect DLP Licensing (Set)
      isOptional: false,
      quantity: 'kit',
      seqNum: 1,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0071',
      hrid: 'SPI0071',
      supplierProduct: sp0029, // AppDirect Data Loss Protection (DLP) (Kit)
      product: pr0077, // SASE Support & Implementation (Set)
      descr: 'SASE Support & Implementation',
      childSupplierProduct: sp0031, // AppDirect SASE Support & Implementation (Set)
      isOptional: false,
      quantity: 'kit',
      seqNum: 2,
      version: '1',
      canBeDeleted: false,
    },
    // Production's amounts, split fractions and quantities. All ANNUAL.
    {
      id: 'XXSPI0072',
      hrid: 'SPI0072',
      supplierProduct: sp0030, // AppDirect DLP Licensing (Set)
      product: pr0078, // Zscaler
      descr: 'Zscaler Data Protection - 1 Feature',
      isOptional: true,
      splitFraction: 0.068,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Annually'),
      paid: getRecursPaidIntervalByLabel('Annually'),
      recursPaidDescr: 'A / A',
      quantity: 'kit',
      unitRcAmount: 43,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2026-10-07',
      seqNum: 1,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0073',
      hrid: 'SPI0073',
      supplierProduct: sp0031, // AppDirect SASE Support & Implementation (Set)
      product: pr0078, // Zscaler
      descr: 'Support Essentials - 1 YR',
      isOptional: true,
      splitFraction: 0.065,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Annually'),
      paid: getRecursPaidIntervalByLabel('Annually'),
      recursPaidDescr: 'A / A',
      quantity: 1,
      unitRcAmount: 6625,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2026-10-07',
      seqNum: 1,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0074',
      hrid: 'SPI0074',
      supplierProduct: sp0031, // AppDirect SASE Support & Implementation (Set)
      product: pr0078, // Zscaler
      descr: 'Support Essentials - 2YR',
      isOptional: true,
      splitFraction: 0.068,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Annually'),
      paid: getRecursPaidIntervalByLabel('Annually'),
      recursPaidDescr: 'A / A',
      quantity: 1,
      unitRcAmount: 49.78,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2026-10-07',
      seqNum: 2,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0075',
      hrid: 'SPI0075',
      supplierProduct: sp0031, // AppDirect SASE Support & Implementation (Set)
      product: pr0078, // Zscaler
      descr: 'Support Essentials - 3YR',
      isOptional: true,
      splitFraction: 0.065,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Annually'),
      paid: getRecursPaidIntervalByLabel('Annually'),
      recursPaidDescr: 'A / A',
      quantity: 'kit',
      unitRcAmount: 5602.13,
      unitNrcAmount: 0,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2026-10-07',
      seqNum: 3,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSPI0076',
      hrid: 'SPI0076',
      supplierProduct: sp0031, // AppDirect SASE Support & Implementation (Set)
      product: pr0078, // Zscaler
      descr: 'Essentials Deployment',
      isOptional: true,
      splitFraction: 0,
      term: null,
      recurs: getRecursPaidIntervalByLabel('Annually'),
      paid: getRecursPaidIntervalByLabel('Annually'),
      recursPaidDescr: 'A / A',
      quantity: 1,
      unitRcAmount: 0,
      unitNrcAmount: 6600,
      oneTimeAmount: 0,
      doMultiplyNrcByQuantity: false,
      doCopyDollarAmountsToServices: true,
      quoteDate: '2026-10-07',
      seqNum: 4,
      status: getSupplierProductItemStatusById(
        SupplierProductItemStatusIds.ACTIVE
      ),
      version: '1',
      canBeDeleted: false,
    },
  ]
}
